import React, { useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Link, Popover, Switch, Tooltip, Typography } from '@mui/material';
import { ChevronRight as ChevronIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { flagDescriptionParts, sentenceCase } from '../../utils/flagDescription';
import { ToneSegmented } from './ToneSegmented';
import { OrgTile } from './OrganizationPicker';
import {
  FeatureFlagDefinition,
  FlagOverrideEntry,
  formatAddedOn,
  GlobalSetting,
  isRecentlyAdded,
  ISSUE_URL_BASE,
  kindKeyOf,
  ResolvedFlagDetail,
  ScopedOrganization,
} from './featureFlagTypes';

/** Flag · Global default · Override; the list header uses the same template. */
export const FLAG_GRID_COLUMNS = 'minmax(0,1fr) 170px 250px';

/** The small tinted tags on a flag's meta line: area, New, Planned, estimate row. */
const MetaChip: React.FC<{ tone: 'accent' | 'caution' | 'neutral' | 'info' | 'positive'; outlined?: boolean; children: React.ReactNode }> = ({
  tone,
  outlined = false,
  children,
}) => {
  const palette =
    tone === 'accent'
      ? { bg: tokens.accent.tint, ink: tokens.accent.main, border: tokens.accent.border }
      : { bg: tokens.signal[tone].tint, ink: tokens.signal[tone].ink, border: tokens.signal[tone].border };
  return (
    <Box
      component="span"
      sx={{
        px: '7px',
        py: '1px',
        borderRadius: '5px',
        bgcolor: outlined ? 'transparent' : palette.bg,
        color: palette.ink,
        fontWeight: 600,
        ...(outlined ? { border: `1px solid ${palette.border}` } : {}),
      }}
    >
      {children}
    </Box>
  );
};

const stateInk = (enabled: boolean) => (enabled ? tokens.signal.positive.ink : tokens.signal.problem.ink);

const entryToOrg = (entry: FlagOverrideEntry): ScopedOrganization => ({
  id: entry.organizationId,
  name: entry.organizationName,
  type: entry.organizationType,
});

/** One organization's override as a small chip: name, then On / Off. Clicking scopes the page to it. */
const OverrideChip: React.FC<{ entry: FlagOverrideEntry; onPick: (org: ScopedOrganization) => void; height?: number }> = ({
  entry,
  onPick,
  height = 24,
}) => {
  const { t } = useAppTranslation();
  return (
    <ButtonBase
      onClick={() => onPick(entryToOrg(entry))}
      title={t('admin.featureFlags.override.chipTitle', { org: entry.organizationName })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        maxWidth: 150,
        height,
        px: 1,
        border: `1px solid ${tokens.line.default}`,
        borderRadius: '6px',
        bgcolor: tokens.surface.card,
        fontSize: 12,
        color: tokens.ink.primary,
        '&:hover': { borderColor: tokens.ink.disabled },
      }}
    >
      <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.organizationName}
      </Box>
      <Box component="span" sx={{ flex: '0 0 auto', fontWeight: 600, color: stateInk(entry.enabled) }}>
        {entry.enabled ? t('admin.featureFlags.states.on') : t('admin.featureFlags.states.off')}
      </Box>
    </ButtonBase>
  );
};

/** "See all n": every override of one flag, fetched in full when the inline list was capped. */
const SeeAllOverrides: React.FC<{
  def: FeatureFlagDefinition;
  globalLabel: string;
  onPick: (org: ScopedOrganization) => void;
}> = ({ def, globalLabel, onPick }) => {
  const { t } = useAppTranslation();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const inline = def.overrides ?? [];
  const count = def.overrideCount ?? inline.length;
  const capped = count > inline.length;

  const full = useQuery({
    queryKey: ['admin-feature-flags-overrides', def.key],
    queryFn: async (): Promise<{ overrides: FlagOverrideEntry[]; overrideCount: number }> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/feature-flags/${def.key}/overrides`, { headers });
      if (!res.ok) throw new Error(t('admin.featureFlags.loadError'));
      const body = await res.json();
      return body.data;
    },
    enabled: open && capped,
    staleTime: 30_000,
  });

  const entries = capped && full.data ? full.data.overrides : inline;
  const shownTotal = capped && full.data ? full.data.overrideCount : count;

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          height: 24,
          px: 0.75,
          borderRadius: '6px',
          fontSize: 12,
          fontWeight: 600,
          color: tokens.accent.main,
          bgcolor: open ? tokens.accent.tint : 'transparent',
          '&:hover': { bgcolor: tokens.accent.tint },
        }}
      >
        {t('admin.featureFlags.override.seeAll', { count })}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 260,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 320,
              overflowY: 'auto',
              p: 0.75,
              borderRadius: '11px',
              border: `1px solid ${tokens.line.default}`,
              boxShadow: tokens.shadow.menu,
            },
          },
        }}
      >
        <Typography
          component="div"
          sx={{ px: 1, pt: 0.5, pb: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}
        >
          {t('admin.featureFlags.override.popoverTitle', { count, state: globalLabel })}
        </Typography>
        {capped && full.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {entries.map((entry) => (
            <ButtonBase
              key={entry.organizationId}
              onClick={() => {
                setOpen(false);
                onPick(entryToOrg(entry));
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1,
                py: '7px',
                borderRadius: '8px',
                textAlign: 'left',
                '&:hover': { bgcolor: tokens.surface.tint },
              }}
            >
              <OrgTile name={entry.organizationName} />
              <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography component="span" noWrap sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>
                  {entry.organizationName}
                </Typography>
                <Typography component="span" sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
                  {t(`admin.featureFlags.kinds.${kindKeyOf(entry.organizationType)}`)}
                </Typography>
              </Box>
              <Typography component="span" sx={{ flex: '0 0 auto', fontSize: 12.5, fontWeight: 600, color: stateInk(entry.enabled) }}>
                {entry.enabled ? t('admin.featureFlags.states.on') : t('admin.featureFlags.states.off')}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
        {shownTotal > entries.length && (
          <Typography sx={{ px: 1, pt: 0.75, fontSize: 11.5, color: tokens.ink.muted }}>
            {t('admin.featureFlags.override.capped', { shown: entries.length, total: shownTotal })}
          </Typography>
        )}
        <Typography
          sx={{ px: 1, pt: 0.75, pb: 0.5, mt: 0.25, borderTop: `1px solid ${tokens.line.hairline}`, fontSize: 11.5, color: tokens.ink.muted }}
        >
          {t('admin.featureFlags.override.popoverFooter')}
        </Typography>
      </Popover>
    </>
  );
};

export interface FlagRowProps {
  def: FeatureFlagDefinition;
  /** The stored platform-wide setting, null when none is stored. */
  globalSetting: GlobalSetting | null;
  /** What everyone without an override gets: the setting, else the definition default. */
  globalValue: GlobalSetting;
  narrow: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  org: ScopedOrganization | null;
  /** The scoped organization's own override, undefined when it has none. */
  orgOverride: boolean | undefined;
  orgDetail: ResolvedFlagDetail | undefined;
  orgLoading: boolean;
  /** A write of this flag's override for the scoped organization is in flight. */
  orgPending: boolean;
  /** A write of this flag's global setting is in flight; no second change until it settles. */
  globalPending: boolean;
  onOrgOverride: (enabled: boolean | null) => void;
  onPickOrg: (org: ScopedOrganization) => void;
  /** The global change awaiting confirmation on this row; undefined when none is. */
  confirmTarget: GlobalSetting | null | undefined;
  onAskGlobal: (target: GlobalSetting | null) => void;
  onCancelGlobal: () => void;
  onConfirmGlobal: () => void;
  totalOrganizations: number | null;
  /** Scope tags on the rebuilt System page. The legacy page leaves them off. */
  showScopes?: boolean;
}

export const FlagRow: React.FC<FlagRowProps> = ({
  def,
  globalSetting,
  globalValue,
  narrow,
  expanded,
  onToggleExpanded,
  org,
  orgOverride,
  orgDetail,
  orgLoading,
  orgPending,
  globalPending,
  onOrgOverride,
  onPickOrg,
  confirmTarget,
  onAskGlobal,
  onCancelGlobal,
  onConfirmGlobal,
  totalOrganizations,
  showScopes = false,
}) => {
  const { t, i18n } = useAppTranslation();
  const parts = flagDescriptionParts(def);
  const planned = def.status === 'planned';
  const overrides = def.overrides ?? [];
  const overrideCount = def.overrideCount ?? overrides.length;
  const overrideOnCount = def.overrideOnCount ?? overrides.filter((entry) => entry.enabled).length;
  const detailId = `flag-detail-${def.key}`;
  const confirming = confirmTarget !== undefined;

  const stateLabel = (value: GlobalSetting) =>
    value === 'plan'
      ? t('admin.featureFlags.states.plan')
      : value
        ? t('admin.featureFlags.states.on')
        : t('admin.featureFlags.states.off');
  const stateWord = (value: boolean) =>
    value ? t('admin.featureFlags.states.onLower') : t('admin.featureFlags.states.offLower');
  const globalLabel = stateLabel(globalValue);
  const defaultValue = def.defaultEnabled === true;

  // ---- Confirm copy -------------------------------------------------------
  const confirmCopy = (() => {
    if (confirmTarget === undefined) return null;
    const name = def.label;
    if (confirmTarget === 'plan') {
      return {
        title: t('admin.featureFlags.confirm.planTitle', { name }),
        body: t('admin.featureFlags.confirm.planBody', { count: overrideCount }),
        cta: t('admin.featureFlags.confirm.ctaPlan'),
      };
    }
    const next = confirmTarget === null ? defaultValue : confirmTarget;
    const title =
      confirmTarget === null
        ? t('admin.featureFlags.confirm.resetTitle', { name, state: stateWord(defaultValue) })
        : next
          ? t('admin.featureFlags.confirm.onTitle', { name })
          : t('admin.featureFlags.confirm.offTitle', { name });
    const cta =
      confirmTarget === null
        ? t('admin.featureFlags.confirm.ctaReset')
        : next
          ? t('admin.featureFlags.confirm.ctaOn')
          : t('admin.featureFlags.confirm.ctaOff');
    if (globalValue === next) {
      return { title, body: t('admin.featureFlags.confirm.noChange'), cta };
    }
    // Overrides already at the new value are unaffected; the rest keep the old value.
    const alreadyThere = next ? overrideOnCount : overrideCount - overrideOnCount;
    const keepers = overrideCount - alreadyThere;
    const pieces = [
      totalOrganizations !== null
        ? t('admin.featureFlags.confirm.applies', { count: Math.max(0, totalOrganizations - overrideCount) })
        : t('admin.featureFlags.confirm.appliesUnknown'),
    ];
    if (keepers > 0) pieces.push(t('admin.featureFlags.confirm.keepers', { count: keepers, state: stateWord(!next) }));
    if (alreadyThere > 0) pieces.push(t('admin.featureFlags.confirm.unaffected', { count: alreadyThere, state: stateWord(next) }));
    return { title, body: pieces.join(' '), cta };
  })();

  // ---- Override cell (organization scoped) --------------------------------
  const selection: 'default' | 'on' | 'off' = orgOverride === true ? 'on' : orgOverride === false ? 'off' : 'default';
  // The value the organization would inherit without its override. With a
  // `plan` setting that depends on its entitlements, known only while it inherits.
  // The resolved detail is trusted only when it was resolved under `plan` too:
  // after an optimistic global change it still describes the old setting.
  const inherited: GlobalSetting =
    globalValue === 'plan' && orgOverride === undefined && orgDetail?.source === 'plan' ? orgDetail.enabled : globalValue;
  // Derived from the override and `inherited`, never from a possibly stale detail.
  const effective: GlobalSetting = orgOverride ?? inherited;
  const effectiveText = (() => {
    if (!org) return '';
    const state = stateLabel(effective);
    if (orgOverride === undefined) {
      return globalValue === 'plan'
        ? t('admin.featureFlags.override.inheritsPlan', { state, org: org.name })
        : t('admin.featureFlags.override.inherits', { state, org: org.name });
    }
    const same = typeof globalValue === 'boolean' && globalValue === orgOverride;
    return `${t('admin.featureFlags.override.overridden', { state, org: org.name })}${same ? t('admin.featureFlags.override.sameAsGlobal') : ''}`;
  })();

  const plannedTip = planned ? t('admin.featureFlags.plannedTooltip') : '';
  const cellIndent = narrow ? '24px' : 0;

  const planReach = (() => {
    if (!def.planFeature) return null;
    const tiers = (def.planTiers ?? []).map((tier) => t(`admin.featureFlags.tiers.${tier}`, { defaultValue: tier }));
    const addons = (def.planAddons ?? []).map((key) => key.replace(/_/g, ' '));
    const base = tiers.length
      ? t('admin.featureFlags.planTiers', { tiers: tiers.join(', ') })
      : t('admin.featureFlags.planTiersNone');
    return addons.length ? `${base} ${t('admin.featureFlags.planAddons', { addons: addons.join(', ') })}` : base;
  })();

  type GlobalChoice = 'default' | 'on' | 'off' | 'plan';
  const globalChoice: GlobalChoice =
    globalSetting === 'plan' ? 'plan' : globalSetting === true ? 'on' : globalSetting === false ? 'off' : 'default';
  const globalChoices = [
    { value: 'default' as const, label: t('admin.featureFlags.detail.globalDefault', { state: stateLabel(defaultValue) }) },
    { value: 'on' as const, label: t('admin.featureFlags.states.on'), tone: 'positive' as const },
    { value: 'off' as const, label: t('admin.featureFlags.states.off'), tone: 'problem' as const },
    ...(def.planFeature ? [{ value: 'plan' as const, label: t('admin.featureFlags.states.plan') }] : []),
  ];

  return (
    <Box
      data-testid="flag-row"
      sx={{ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${tokens.line.hairline}`, bgcolor: expanded ? tokens.surface.subtle : 'transparent' }}
    >
      <Box
        sx={{
          display: 'grid',
          // The card's measured width (ResizeObserver) collapses the row; the breakpoint
          // object is the fallback before it is measured and on a phone.
          gridTemplateColumns: narrow ? 'minmax(0,1fr)' : { xs: 'minmax(0,1fr)', md: FLAG_GRID_COLUMNS },
          alignItems: 'center',
          gap: narrow ? '10px' : 2,
          px: '18px',
          py: '14px',
        }}
      >
        {/* Flag: the whole cell expands the row. */}
        <ButtonBase
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-controls={detailId}
          sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.25, textAlign: 'left', borderRadius: '6px' }}
        >
          <ChevronIcon
            sx={{
              flex: '0 0 auto',
              mt: '2px',
              fontSize: 16,
              color: tokens.ink.faint,
              transform: expanded ? 'rotate(90deg)' : 'none',
              transition: 'transform .15s',
            }}
          />
          <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink.primary }}>
                {def.label}
              </Typography>
              <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted, wordBreak: 'break-all' }}>
                {def.key}
              </Typography>
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: 13,
                lineHeight: 1.45,
                color: tokens.ink.bodyAlt,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {sentenceCase(parts.on)}
            </Typography>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12, color: tokens.ink.muted }}>
              {def.area && (
                <MetaChip tone="accent" outlined={showScopes}>
                  {t(`admin.featureFlags.areas.${def.area}`)}
                </MetaChip>
              )}
              {showScopes &&
                (def.scopes || []).map((scope) => (
                  <MetaChip key={scope} tone={scope === 'store' ? 'info' : scope === 'vendor' ? 'positive' : 'neutral'}>
                    {t(`admin.featureFlags.scopes.${scope}`)}
                  </MetaChip>
                ))}
              {def.addedOn && (
                <span>{t('admin.featureFlags.addedOn', { date: formatAddedOn(def.addedOn, i18n.language) })}</span>
              )}
              {isRecentlyAdded(def.addedOn) && <MetaChip tone="caution">{t('admin.featureFlags.new')}</MetaChip>}
              {planned && <MetaChip tone="caution">{t('admin.featureFlags.planned')}</MetaChip>}
              {def.estimateRow && (
                <MetaChip tone="neutral">{t('admin.featureFlags.estimateRow', { row: def.estimateRow })}</MetaChip>
              )}
            </Box>
          </Box>
        </ButtonBase>

        {/* Global default: the switch asks before it changes anything. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pl: cellIndent }}>
          <Tooltip title={plannedTip}>
            <span>
              <Switch
                checked={globalValue === true}
                disabled={planned || globalPending}
                onChange={() => onAskGlobal(globalValue === true ? false : true)}
                slotProps={{ input: { role: 'switch', 'aria-label': t('admin.featureFlags.global.switchLabel', { name: def.label }) } }}
                disableRipple
                sx={{
                  width: 36,
                  height: 20,
                  p: 0,
                  '& .MuiSwitch-switchBase': {
                    p: '2px',
                    color: tokens.ink.inverse,
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: tokens.ink.inverse,
                      '& + .MuiSwitch-track': { bgcolor: tokens.signal.positive.solid, opacity: 1 },
                    },
                    '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
                    '&.Mui-focusVisible .MuiSwitch-thumb': { outline: `2px solid ${tokens.accent.main}`, outlineOffset: 2 },
                  },
                  '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: tokens.shadow.card },
                  '& .MuiSwitch-track': { borderRadius: '10px', bgcolor: tokens.ink.disabled, opacity: 1, transition: 'background-color .15s' },
                }}
              />
            </span>
          </Tooltip>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>
              {globalLabel}
            </Typography>
            <Typography component="span" sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
              {globalValue === 'plan' ? t('admin.featureFlags.global.forPlans') : t('admin.featureFlags.global.forEveryone')}
            </Typography>
          </Box>
        </Box>

        {/* Override: the scoped organization's choice, or who has overridden it. */}
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px', pl: cellIndent }}>
          {org ? (
            <>
              <Tooltip title={plannedTip}>
                <span>
                  <ToneSegmented
                    aria-label={t('admin.featureFlags.override.label', { name: def.label, org: org.name })}
                    value={selection}
                    disabled={planned || orgLoading || orgPending}
                    onChange={(next) => onOrgOverride(next === 'default' ? null : next === 'on')}
                    options={[
                      { value: 'default', label: t('admin.featureFlags.override.default', { state: stateLabel(inherited) }) },
                      { value: 'on', label: t('admin.featureFlags.states.on'), tone: 'positive' },
                      { value: 'off', label: t('admin.featureFlags.states.off'), tone: 'problem' },
                    ]}
                  />
                </span>
              </Tooltip>
              {!orgLoading && (
                <Typography component="span" sx={{ fontSize: 11.5, color: orgOverride === undefined ? tokens.ink.muted : tokens.ink.primary }}>
                  {effectiveText}
                </Typography>
              )}
            </>
          ) : overrideCount === 0 ? (
            <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.faint }}>
              {t('admin.featureFlags.override.none')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              {(overrideCount > 2 ? overrides.slice(0, 1) : overrides.slice(0, 2)).map((entry) => (
                <OverrideChip key={entry.organizationId} entry={entry} onPick={onPickOrg} />
              ))}
              {overrideCount > 2 && <SeeAllOverrides def={def} globalLabel={globalLabel} onPick={onPickOrg} />}
            </Box>
          )}
        </Box>
      </Box>

      {confirmCopy && confirming && (
        <Box
          role="alertdialog"
          aria-label={confirmCopy.title}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mx: '18px',
            ml: narrow ? '18px' : '42px',
            mb: '14px',
            px: '14px',
            py: '11px',
            border: `1px solid ${tokens.line.default}`,
            borderRadius: '10px',
            bgcolor: tokens.surface.card,
            boxShadow: tokens.shadow.card,
          }}
        >
          <Typography component="span" sx={{ flex: '1 1 280px', fontSize: 13, color: tokens.ink.body }}>
            <Box component="b" sx={{ color: tokens.ink.primary }}>
              {confirmCopy.title}
            </Box>{' '}
            {confirmCopy.body}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancelGlobal}
            sx={{ color: tokens.ink.body, borderColor: tokens.line.default, '&:hover': { borderColor: tokens.ink.disabled, bgcolor: tokens.surface.sunken } }}
          >
            {t('admin.featureFlags.confirm.cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            autoFocus
            disabled={globalPending}
            onClick={onConfirmGlobal}
            sx={{ bgcolor: tokens.surface.header, '&:hover': { bgcolor: tokens.ink.body } }}
          >
            {confirmCopy.cta}
          </Button>
        </Box>
      )}

      {expanded && (
        <Box
          id={detailId}
          sx={{
            mx: '18px',
            ml: narrow ? '18px' : '42px',
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
            px: 2,
            py: '14px',
            borderRadius: '10px',
            bgcolor: tokens.surface.sunken,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(auto-fit, minmax(260px, 1fr))' }, gap: '18px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.signal.positive.ink }}>
                {t('admin.featureFlags.detail.whenOn')}
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.5, color: tokens.ink.body }}>
                {sentenceCase(parts.on)}
              </Typography>
            </Box>
            {parts.off && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                  {t('admin.featureFlags.detail.whenOff')}
                </Typography>
                <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.5, color: tokens.ink.body }}>
                  {sentenceCase(parts.off)}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap', fontSize: 12.5, color: tokens.ink.muted }}>
            <span>{t('admin.featureFlags.detail.globalSetting')}</span>
            <Tooltip title={plannedTip}>
              <span>
                <ToneSegmented
                  aria-label={t('admin.featureFlags.detail.globalSettingLabel', { name: def.label })}
                  value={globalChoice}
                  disabled={planned || globalPending}
                  onChange={(next) => onAskGlobal(next === 'default' ? null : next === 'plan' ? 'plan' : next === 'on')}
                  options={globalChoices}
                />
              </span>
            </Tooltip>
            {planReach && <span>{planReach}</span>}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px 18px',
              flexWrap: 'wrap',
              pt: 1.5,
              borderTop: `1px solid ${tokens.line.default}`,
              fontSize: 12.5,
              color: tokens.ink.muted,
            }}
          >
            {parts.commercialHome && (
              <span>
                {t('admin.featureFlags.detail.commercialHome')}{' '}
                <Box component="b" sx={{ color: tokens.ink.primary, fontWeight: 600 }}>
                  {parts.commercialHome}
                </Box>
                {parts.commercialHomeNote ? ` ${parts.commercialHomeNote}` : ''}
              </span>
            )}
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {t('admin.featureFlags.detail.overrides')}
              {overrides.map((entry) => (
                <OverrideChip key={entry.organizationId} entry={entry} onPick={onPickOrg} height={22} />
              ))}
              {overrideCount === 0 && (
                <Box component="b" sx={{ color: tokens.ink.primary, fontWeight: 600 }}>
                  {t('admin.featureFlags.detail.none')}
                </Box>
              )}
              {overrideCount > overrides.length && (
                <span>{t('admin.featureFlags.override.capped', { shown: overrides.length, total: overrideCount })}</span>
              )}
            </Box>
            {def.issue && (
              <Link href={`${ISSUE_URL_BASE}${def.issue}`} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12.5 }}>
                {t('admin.featureFlags.issueLink', { number: def.issue })}
              </Link>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
